// src/views/saas/SaasDashboard.jsx
import React, { useState, useEffect, useMemo } from "react";
import saasService from "../../services/saasService";

const fmtM = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtD = d => { try{ const s=(d||"").split("T")[0]; return s?new Date(s+"T12:00:00").toLocaleDateString("pt-BR"):"—"; }catch{ return "—"; } };
const fmtDT = d => { try{ return new Date(d).toLocaleString("pt-BR"); }catch{ return "—"; } };

const STATUS_COLOR = {
  Ativo:       {bg:"#DCFCE7",color:"#166534"},
  GracePeriod: {bg:"#FEF9C3",color:"#854D0E"},
  Bloqueado:   {bg:"#FEE2E2",color:"#991B1B"},
  Cancelado:   {bg:"#F1F5F9",color:"var(--gray-600)"},
};

const MES_LABEL = (ym) => {
  const [y,m] = (ym||"").split("-");
  const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  return m ? `${meses[parseInt(m)-1]}/${y.slice(-2)}` : ym;
};

export default function SaasDashboard({ onNavigate }) {
  const [stats, setStats]     = useState(null);
  const [planos, setPlanos]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([saasService.saasStats(), saasService.listarPlanos()])
      .then(([st, pl]) => {
        setStats(st);
        setPlanos(pl.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totais   = stats?.totais || {};
  const mrr      = stats?.mrr || 0;
  const arr      = mrr * 12;
  const ticket   = totais.ativos ? mrr / totais.ativos : 0;
  const semSetup = stats?.semSetup || [];
  const vencProx = stats?.vencProximos || [];
  const planosDist = stats?.planosDist || [];
  const evolucao   = stats?.evolucao || [];
  const acoes      = stats?.acoesRecentes || [];
  const novosMes   = stats?.clientesNovos || [];

  // % crescimento mês vs mês anterior
  const crescimento = useMemo(() => {
    if (evolucao.length < 2) return null;
    const atual    = evolucao[evolucao.length-1]?.novos || 0;
    const anterior = evolucao[evolucao.length-2]?.novos || 0;
    if (!anterior) return null;
    return Math.round(((atual - anterior) / anterior) * 100);
  }, [evolucao]);

  const maxEvol = Math.max(1, ...evolucao.map(e => e.novos || 0));

  if (loading) {
    return <div style={{textAlign:"center",padding:60}}><span className="spinner-border text-primary"/></div>;
  }

  const cards = [
    { label:"Total de Clientes", value:totais.total||0,       icon:"bi-people-fill",      color:"#1D4ED8", bg:"#EFF6FF" },
    { label:"Ativos",            value:totais.ativos||0,      icon:"bi-check-circle-fill",color:"#166534", bg:"#DCFCE7" },
    { label:"Grace Period",      value:totais.gracePeriod||0, icon:"bi-clock-fill",       color:"#854D0E", bg:"#FEF9C3" },
    { label:"Bloqueados",        value:totais.bloqueados||0,  icon:"bi-slash-circle-fill",color:"#991B1B", bg:"#FEE2E2" },
    { label:"MRR",               value:fmtM(mrr),             icon:"bi-currency-dollar",  color:"#166534", bg:"#DCFCE7" },
    { label:"ARR (projetado)",   value:fmtM(arr),             icon:"bi-graph-up-arrow",   color:"#0E7490", bg:"#CFFAFE" },
    { label:"Ticket Médio",      value:fmtM(ticket),          icon:"bi-cash-stack",       color:"#7C3AED", bg:"#F5F3FF" },
    { label:"Planos Ativos",     value:planos.filter(p=>p.ativo).length, icon:"bi-box-seam", color:"#0E7490", bg:"#ECFEFF" },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-speedometer2 me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Admin SaaS — Visão Geral
          </h1>
          <p className="page-subtitle">Painel de controle da plataforma Horizon eCondo</p>
        </div>
      </div>

      {/* ─── Alertas críticos ─── */}
      {(totais.vencidos > 0 || vencProx.length > 0) && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10,marginBottom:16}}>
          {totais.vencidos > 0 && (
            <div style={{background:"var(--bg-danger-soft)",border:"1px solid #FCA5A5",borderRadius:10,padding:"12px 14px",cursor:"pointer"}}
              onClick={()=>onNavigate?.("saas-clientes")}>
              <div style={{fontSize:11,color:"#991B1B",fontWeight:700,textTransform:"uppercase"}}>
                <i className="bi bi-exclamation-octagon-fill me-1"/>Vencidos
              </div>
              <div style={{fontSize:22,fontWeight:800,color:"#991B1B",marginTop:4}}>{totais.vencidos}</div>
              <div style={{fontSize:11,color:"#7F1D1D"}}>clientes com licença vencida</div>
            </div>
          )}
          {vencProx.length > 0 && (
            <div style={{background:"var(--bg-warning-soft)",border:"1px solid #FDE68A",borderRadius:10,padding:"12px 14px",cursor:"pointer"}}
                    onClick={()=>onNavigate?.("saas-clientes")}>
              <div style={{fontSize:11,color:"#854D0E",fontWeight:700,textTransform:"uppercase"}}>
                <i className="bi bi-calendar-event-fill me-1"/>Vencimentos em 15 dias
              </div>
              <div style={{fontSize:22,fontWeight:800,color:"#854D0E",marginTop:4}}>{vencProx.length}</div>
              <div style={{fontSize:11,color:"#92400E"}}>contratos a renovar</div>
            </div>
          )}
          {semSetup.length > 0 && (
            <div style={{background:"#FFEDD5",border:"1px solid #FED7AA",borderRadius:10,padding:"12px 14px",cursor:"pointer"}}
              onClick={()=>onNavigate?.("saas-clientes")}>
              <div style={{fontSize:11,color:"#9A3412",fontWeight:700,textTransform:"uppercase"}}>
                <i className="bi bi-tools me-1"/>Setup pendente
              </div>
              <div style={{fontSize:22,fontWeight:800,color:"#9A3412",marginTop:4}}>{semSetup.length}</div>
              <div style={{fontSize:11,color:"#7C2D12"}}>clientes com setup incompleto</div>
          </div>
          )}
        </div>
      )}

      {/* ─── KPI Cards ─── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))",gap:12,marginBottom:20}}>
        {cards.map(card => (
          <div key={card.label} className="card" style={{padding:16}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:42,height:42,borderRadius:10,background:card.bg,
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <i className={`bi ${card.icon}`} style={{fontSize:20,color:card.color}}/>
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:20,fontWeight:700,color:"var(--gray-800)",lineHeight:1.1}}>{card.value}</div>
                <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2}}>{card.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16,marginBottom:16}}>
        {/* ─── Evolução de Contratações ─── */}
        <div className="card" style={{padding:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
              <i className="bi bi-graph-up me-1" style={{color:"var(--primary)"}}/>
              Novos Clientes (últimos 6 meses)
            </div>
            {crescimento != null && (
              <span style={{fontSize:12,fontWeight:700,
                color: crescimento>=0 ? "#166534":"#991B1B",
                background: crescimento>=0 ? "#DCFCE7":"#FEE2E2",
                padding:"2px 10px",borderRadius:10}}>
                {crescimento>=0?"+":""}{crescimento}% vs mês anterior
              </span>
            )}
          </div>
          {evolucao.length === 0 ? (
            <div style={{textAlign:"center",padding:30,color:"var(--gray-400)",fontSize:13}}>
              Sem dados de contratações ainda.
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"flex-end",gap:8,height:140,paddingTop:10}}>
              {evolucao.map((e,i) => {
                const h = Math.max(4, (e.novos / maxEvol) * 120);
                return (
                  <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--primary)"}}>{e.novos}</div>
                    <div style={{width:"100%",height:h,background:"linear-gradient(180deg,#3B82F6,#1D4ED8)",
                      borderRadius:"4px 4px 0 0",minHeight:4}}/>
                    <div style={{fontSize:10,color:"var(--gray-500)",fontWeight:600}}>{MES_LABEL(e.mes)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── Distribuição por Plano ─── */}
        <div className="card" style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)",marginBottom:12}}>
            <i className="bi bi-pie-chart-fill me-1" style={{color:"var(--primary)"}}/>
            Distribuição por Plano
          </div>
          {planosDist.length === 0 ? (
            <div style={{textAlign:"center",padding:20,color:"var(--gray-400)",fontSize:13}}>
              Nenhum cliente ativo.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {planosDist.map(p => {
                const total = planosDist.reduce((s,x)=>s+(x.qtd||0),0);
                const pct = total ? Math.round((p.qtd/total)*100) : 0;
                return (
                  <div key={p.nmPlano}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                      <span style={{fontWeight:600,color:"var(--gray-700)"}}>
                        <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:p.cor||"#94A3B8",marginRight:6}}/>
                        {p.nmPlano}
                      </span>
                      <span style={{color:"var(--gray-500)"}}>{p.qtd} · {pct}%</span>
                    </div>
                    <div style={{height:6,background:"var(--bg-page)",borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:p.cor||"#94A3B8"}}/>
                    </div>
                    {p.receita>0 && <div style={{fontSize:10,color:"var(--gray-400)",marginTop:2}}>{fmtM(p.receita)}/mês</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ─── Vencimentos próximos + Setup pendente lado a lado ─── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div className="card" style={{padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
              <i className="bi bi-calendar-event me-1" style={{color:"#B45309"}}/>
              Vencimentos próximos
            </div>
            {vencProx.length>0 && (
              <button className="btn btn-link btn-sm p-0" onClick={()=>onNavigate?.("saas-clientes")}>
                Ver todos →
              </button>
            )}
          </div>
          {vencProx.length === 0 ? (
            <div style={{padding:14,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
              Nenhum vencimento nos próximos 15 dias.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {vencProx.slice(0,8).map(v => {
                const cor = v.diasVencimento<=3 ? "#991B1B" : v.diasVencimento<=7 ? "#854D0E" : "#475569";
                const bg  = v.diasVencimento<=3 ? "#FEE2E2" : v.diasVencimento<=7 ? "#FEF9C3" : "#F1F5F9";
                return (
                  <div key={v.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    padding:"6px 10px",borderRadius:6,background:bg,fontSize:12,cursor:"pointer"}}
                    onClick={()=>onNavigate?.("saas-clientes")}>
                    <span style={{fontWeight:600,color:cor}}>{v.nmCliente}</span>
                    <span style={{fontSize:11,color:cor,fontWeight:700}}>
                      {v.diasVencimento===0?"vence hoje":`${v.diasVencimento}d`} · {fmtD(v.dtVencimento)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card" style={{padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
              <i className="bi bi-tools me-1" style={{color:"#9A3412"}}/>
              Setup Pendente
            </div>
            {semSetup.length>0 && (
              <button className="btn btn-link btn-sm p-0" onClick={()=>onNavigate?.("saas-clientes")}>
                Ver todos →
              </button>
            )}
          </div>
          {semSetup.length === 0 ? (
            <div style={{padding:14,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
              Todos os clientes com setup completo. 🎉
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {semSetup.slice(0,8).map(cl => (
                <div key={cl.id} style={{padding:"6px 10px",borderRadius:6,background:"#FFF7ED",fontSize:12,cursor:"pointer"}}
                  onClick={()=>onNavigate?.("saas-clientes")}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <span style={{fontWeight:600,color:"var(--text-primary)"}}>{cl.nmCliente}</span>
                    <span style={{fontSize:11,color:"#92400E",fontWeight:700}}>{cl.pct||0}%</span>
                  </div>
                  <div style={{fontSize:10,color:"#92400E"}}>
                    {[
                      cl.passosConcluidos?.[0] ? null : "🏢 Sem condomínio",
                      cl.passosConcluidos?.[1] ? null : "🏦 Sem conta bancária",
                      cl.passosConcluidos?.[2] ? null : "🚪 Sem unidades",
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Ações recentes + Novos clientes do mês ─── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div className="card" style={{padding:16}}>
          <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)",marginBottom:12}}>
            <i className="bi bi-clock-history me-1" style={{color:"var(--primary)"}}/>
            Ações recentes
          </div>
          {acoes.length === 0 ? (
            <div style={{padding:14,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
              Nenhuma ação registrada.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:240,overflowY:"auto"}}>
              {acoes.map(a => (
                <div key={a.id} style={{padding:"6px 10px",fontSize:12,borderBottom:"1px solid var(--gray-100)"}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{fontWeight:700,color:"var(--gray-700)"}}>{a.nmCliente}</span>
                    <span style={{fontSize:10,color:"var(--gray-400)"}}>{fmtDT(a.createdAt)}</span>
                  </div>
                  <div style={{fontSize:11,color:"var(--gray-500)"}}>
                    <span style={{fontWeight:600,color:"#1D4ED8"}}>{a.tpAcao}</span>
                    {a.descricao && ` — ${a.descricao}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{padding:16}}>
          <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)",marginBottom:12}}>
            <i className="bi bi-person-plus me-1" style={{color:"var(--primary)"}}/>
            Novos clientes este mês
          </div>
          {novosMes.length === 0 ? (
            <div style={{padding:14,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
              Nenhum cliente cadastrado neste mês.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:240,overflowY:"auto"}}>
              {novosMes.map(n => (
                <div key={n.id} style={{padding:"6px 10px",fontSize:12,borderBottom:"1px solid var(--gray-100)"}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{fontWeight:700,color:"var(--gray-700)"}}>{n.nmCliente}</span>
                    <span style={{fontSize:10,color:"var(--gray-400)"}}>{fmtD(n.createdAt)}</span>
                  </div>
                  <div style={{fontSize:11,color:"var(--gray-500)"}}>{n.email}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Planos Disponíveis (resumo) ─── */}
      {planos.filter(p=>p.ativo).length > 0 && (
        <div className="card" style={{padding:18,marginTop:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>
              <i className="bi bi-box-seam me-1" style={{color:"var(--primary)"}}/>
            Planos Disponíveis
            </div>
            <button className="btn btn-link btn-sm p-0" onClick={()=>onNavigate?.("saas-planos")}>
              Gerenciar planos →
            </button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
            {planos.filter(p=>p.ativo).map(p => (
              <div key={p.id} style={{border:"1px solid var(--gray-200)",borderRadius:8,
                padding:"10px 14px",borderLeft:`4px solid ${p.cor||"var(--primary)"}`}}>
                <div style={{fontWeight:700,fontSize:13,color:"var(--gray-800)"}}>{p.nmPlano}</div>
                <div style={{fontSize:13,color:"var(--primary)",fontWeight:700,marginTop:4}}>
                  {fmtM(p.preco)}/{p.periodo==="Mensal"?"mês":p.periodo?.toLowerCase()||"mês"}
                </div>
                <div style={{fontSize:11,color:"var(--gray-400)",marginTop:4}}>
                  {p.maxCondominios>=999?"Ilimitado":`Até ${p.maxCondominios}`} cond. · {p.maxUsuarios>=50?"Ilimitado":`Até ${p.maxUsuarios}`} usuários
                </div>
                {p.qtdClientes>0 && (
                  <div style={{fontSize:11,color:"#1D4ED8",fontWeight:600,marginTop:4}}>
                    <i className="bi bi-people me-1"/>{p.qtdClientes} cliente(s)
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
